<template>
  <div
    v-if="group.type === 'group' && group.inputs.length > 0"
    class="workflow-input-item group relative py-2 px-3 rounded-lg bg-surface-0 transition-all duration-200"
  >
    <!-- 分组内的多个图片输入 -->
    <div class="file-input-grid-wrapper">
      <div class="file-input-grid">
        <template v-for="(input, index) in group.inputs" :key="input.id">
          <div
            v-show="shouldShowInputInGroup(group.inputs, index)"
            class="file-upload-group-item"
            :class="{
              'file-upload-group-item-with-swap':
                index === 0 &&
                canShowSwapButton &&
                shouldShowInputInGroup(group.inputs, 1),
              'drag-over':
                dragOverInputId === input.id &&
                shouldShowFileSelector(input) &&
                !isEditMode
            }"
            @dragover.prevent="
              shouldShowFileSelector(input) && !isEditMode
                ? handleDragOver($event, input.id)
                : null
            "
            @dragleave.prevent="
              shouldShowFileSelector(input) && !isEditMode
                ? handleDragLeave($event)
                : null
            "
            @drop.prevent.stop="
              shouldShowFileSelector(input) && !isEditMode
                ? handleDrop($event, input)
                : null
            "
          >
            <!-- 单个图片输入的内容 -->
            <div class="mb-2">
              <!-- 输入框标题 -->
              <div class="flex items-center gap-1.5 mb-1">
                <h4
                  class="font-bold text-surface-900 text-base cursor-pointer hover:text-primary-600 transition-colors"
                >
                  {{ input.name }}
                </h4>
                <!-- 说明图标 -->
                <i
                  v-if="input.description"
                  v-tooltip.bottom="input.description"
                  class="pi pi-question-circle text-sm text-surface-400 cursor-help hover:text-primary-500 transition-colors"
                ></i>
              </div>
              <WorkflowFileUpload
                :input="input"
                :input-value="inputValues[input.id]"
                :file-metadata="fileMetadata[input.id]"
                :upload-progress="uploadProgress[input.id] || 0"
                :is-edit-mode="isEditMode"
                :image-url="getImageUrl(inputValues[input.id], input.id)"
                :file-url="getFileUrl(inputValues[input.id])"
                :hovered-upload-input-id="hoveredUploadInputId"
                :hovered-preview-input-id="hoveredPreviewInputId"
                @file-upload="(e) => handleFileUpload(e, input)"
                @open-file-selector="openFileSelector(input)"
                @trigger-file-upload="triggerFileUpload(input)"
                @clear-file-value="clearFileValue(input.id)"
                @open-image-viewer="openImageViewer(input.id)"
                @open-crop-existing-image="openCropExistingImage(input.id)"
                @open-trim-existing-audio="openTrimExistingAudio(input.id)"
                @open-trim-existing-video="openTrimExistingVideo(input.id)"
                @paste-image="(file) => $emit('paste-image', file, input)"
                @update:hovered-upload-input-id="
                  (val) => $emit('update:hovered-upload-input-id', val)
                "
                @update:hovered-preview-input-id="
                  (val) => $emit('update:hovered-preview-input-id', val)
                "
              />
            </div>
            <!-- 以第一张图为参照，悬浮在两图正中间的互换图标 -->
            <i
              v-if="
                index === 0 &&
                canShowSwapButton &&
                shouldShowInputInGroup(group.inputs, 1)
              "
              class="pi pi-arrow-right-arrow-left swap-images-icon"
              :aria-label="t('workflowApps.swapImages')"
              role="button"
              tabindex="0"
              @click="emitSwapImages"
              @keydown.enter="emitSwapImages"
              @keydown.space.prevent="emitSwapImages"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import WorkflowFileUpload from './WorkflowFileUpload.vue'

const { t } = useI18n()

interface WorkflowInput {
  id: string
  name: string
  type: string
  value: any
  description?: string
  placeholder?: string
  options?: any[]
  order?: number
  config: {
    min?: number
    max?: number
    step?: number
    defaultValue?: any
    required?: boolean
    order?: number
    llmPrompt?: string
    [key: string]: any
  }
}

type FileMetadataItem = {
  name?: string
  obsUrl?: string
  originalFilename?: string
  fileId?: number
  previewUrl?: string
  isUploading?: boolean
}

const props = withDefaults(
  defineProps<{
    group: {
      type: 'group' | 'single'
      inputs: WorkflowInput[]
      groupName?: string
      alwaysShowAll?: boolean // 首/尾 分组：不隐藏第二个，始终全部显示
    }
    inputValues?: Record<string, any>
    fileMetadata?: Record<string, FileMetadataItem>
    uploadProgress?: Record<string, number>
    isEditMode?: boolean
    dragOverInputId?: string
    hoveredUploadInputId?: string | null
    hoveredPreviewInputId?: string | null
    getImageUrl?: (filename: string, inputId?: string) => string
    getFileUrl?: (filename: string) => string
  }>(),
  {
    inputValues: () => ({}),
    fileMetadata: () => ({}),
    uploadProgress: () => ({}),
    isEditMode: false,
    dragOverInputId: '',
    hoveredUploadInputId: null,
    hoveredPreviewInputId: null,
    getImageUrl: () => '',
    getFileUrl: () => ''
  }
)

const emit = defineEmits<{
  'file-upload': [event: Event, input: WorkflowInput]
  'open-file-selector': [input: WorkflowInput]
  'trigger-file-upload': [input: WorkflowInput]
  'clear-file-value': [inputId: string]
  'open-image-viewer': [inputId: string]
  'open-crop-existing-image': [inputId: string]
  'open-trim-existing-audio': [inputId: string]
  'open-trim-existing-video': [inputId: string]
  'paste-image': [file: File, input: WorkflowInput]
  'handle-drag-over': [event: DragEvent, inputId: string]
  'handle-drag-leave': [event: DragEvent]
  'handle-drop': [event: DragEvent, input: WorkflowInput]
  'swap-images': [inputId1: string, inputId2: string]
  'update:hovered-upload-input-id': [value: string | null]
  'update:hovered-preview-input-id': [value: string | null]
}>()

// 两个图片输入且非编辑模式时显示互换按钮
const canShowSwapButton = computed(() => {
  if (props.isEditMode || props.group.inputs.length !== 2) return false
  return props.group.inputs.every((i) => i.type === 'file.image')
})

const emitSwapImages = () => {
  if (props.group.inputs.length >= 2) {
    emit('swap-images', props.group.inputs[0].id, props.group.inputs[1].id)
  }
}

const shouldShowFileSelector = (input: WorkflowInput): boolean => {
  return (
    input.type === 'file.image' ||
    input.type === 'file.audio' ||
    input.type === 'file.video'
  )
}

const shouldShowInputInGroup = (
  inputs: WorkflowInput[],
  index: number
): boolean => {
  // 编辑模式下显示所有输入
  if (props.isEditMode) {
    return true
  }

  // 首/尾 分组：不隐藏第二个，始终全部显示
  if (props.group.alwaysShowAll) {
    return true
  }

  // 第一个总是显示
  if (index === 0) return true

  // 找到最后一个有值的索引
  let lastFilledIndex = -1
  for (let i = inputs.length - 1; i >= 0; i--) {
    const inputId = inputs[i].id
    const hasValue =
      props.inputValues[inputId] && !props.fileMetadata[inputId]?.isUploading
    if (hasValue) {
      lastFilledIndex = i
      break
    }
  }

  // 如果所有输入都没有值，只显示第一个
  if (lastFilledIndex === -1) {
    return index === 0
  }

  // 显示从第一个到最后一个有值索引+1的所有输入
  return index <= lastFilledIndex + 1
}

const handleFileUpload = (event: Event, input: WorkflowInput) => {
  emit('file-upload', event, input)
}

const openFileSelector = (input: WorkflowInput) => {
  emit('open-file-selector', input)
}

const triggerFileUpload = (input: WorkflowInput) => {
  emit('trigger-file-upload', input)
}

const clearFileValue = (inputId: string) => {
  emit('clear-file-value', inputId)
}

const openImageViewer = (inputId: string) => {
  emit('open-image-viewer', inputId)
}

const openCropExistingImage = (inputId: string) => {
  emit('open-crop-existing-image', inputId)
}

const openTrimExistingAudio = (inputId: string) => {
  emit('open-trim-existing-audio', inputId)
}

const openTrimExistingVideo = (inputId: string) => {
  emit('open-trim-existing-video', inputId)
}

const handleDragOver = (event: DragEvent, inputId: string) => {
  emit('handle-drag-over', event, inputId)
}

const handleDragLeave = (event: DragEvent) => {
  emit('handle-drag-leave', event)
}

const handleDrop = (event: DragEvent, input: WorkflowInput) => {
  emit('handle-drop', event, input)
}

const getImageUrl = (filename: string, inputId?: string) => {
  if (props.getImageUrl) {
    return props.getImageUrl(filename, inputId)
  }
  return ''
}

const getFileUrl = (filename: string) => {
  if (props.getFileUrl) {
    return props.getFileUrl(filename)
  }
  return ''
}
</script>

<style scoped>
.file-input-grid-wrapper {
  position: relative;
}

.file-input-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 140px));
  gap: 0.75rem;
  justify-content: start;
}

/* 第一张图作为定位参照，需 relative */
.file-upload-group-item-with-swap {
  position: relative;
}

/* 以第一张图为参照，悬浮在两图正中间；right 使图标中心对齐两图间隙中点 */
.swap-images-icon {
  position: absolute;
  right: -1.25rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  font-size: 0.875rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0.5rem;
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;
}

.swap-images-icon:hover {
  background: #fbbf24;
  color: #fff;
}

.workflow-input-item {
  transition: all 0.2s ease;
}

.workflow-input-item:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.workflow-input-item h4 {
  color: var(--p-text-color, #fff);
  margin-top: 0;
  margin-bottom: 0.125rem;
}

.file-upload-group-item.drag-over {
  border-color: var(--p-primary-color) !important;
  background-color: rgba(
    var(--p-primary-color-rgb, 102, 126, 234),
    0.1
  ) !important;
  transform: scale(1.01);
  box-shadow: 0 4px 12px rgba(var(--p-primary-color-rgb, 102, 126, 234), 0.2) !important;
}
</style>
